import { CheckCircle, Warning } from '@mui/icons-material'
import { Alert, Box, Button, LinearProgress, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import type { ImportProgressUpdate } from '@app/src/types/tabularImport'

export type CompleteStepProps = {
  status?: string
  entityNounPlural: string
  completeButtonLabel: string
  onComplete: () => void
  onEnrichMissingProperties?: () => void
  hasEnrichmentAutomation?: boolean
  importProgress?: Omit<ImportProgressUpdate, 'sessionId'> | null
  importResult?: {
    createdCount: number
    updatedCount: number
    failedCount: number
    warnings: string[]
  } | null
}

// "providers" → "provider" for a count of one; the callers pass a regular plural noun.
const pluralize = (nounPlural: string, count: number) => (count === 1 ? nounPlural.replace(/s$/, '') : nounPlural)

const CompleteStep: React.FC<CompleteStepProps> = ({
  status,
  entityNounPlural,
  completeButtonLabel,
  onComplete,
  onEnrichMissingProperties,
  hasEnrichmentAutomation,
  importProgress,
  importResult,
}) => {
  const showEnrichmentOption = !hasEnrichmentAutomation && onEnrichMissingProperties
  const { formatMessage } = useIntl()

  // Importing in progress but no SignalR data yet (e.g., page refresh during import)
  if (status === 'Importing' && !importProgress && !importResult) {
    return (
      <Stack spacing={4} alignItems="center">
        <Typography variant="h4">Importing {entityNounPlural}...</Typography>
        <Box width="100%" maxWidth={500}>
          <LinearProgress sx={{ height: 8, borderRadius: 4 }} />
          <Typography variant="body2" color="text.secondary" textAlign="center" mt={1}>
            Import is running in the background...
          </Typography>
        </Box>
      </Stack>
    )
  }

  // Importing in progress — show progress bar
  if (importProgress) {
    const percent =
      importProgress.totalRows > 0 ? Math.round((importProgress.processedRows / importProgress.totalRows) * 100) : 0

    return (
      <Stack spacing={4} alignItems="center">
        <Typography variant="h4">Importing {entityNounPlural}...</Typography>
        <Box width="100%" maxWidth={500}>
          <LinearProgress variant="determinate" value={percent} sx={{ height: 8, borderRadius: 4 }} />
          <Typography variant="body2" color="text.secondary" textAlign="center" mt={1}>
            {importProgress.processedRows} / {importProgress.totalRows} rows processed
          </Typography>
        </Box>
        <Stack direction="row" spacing={3}>
          <Typography variant="body1" color="success.main">
            {importProgress.createdCount} created
          </Typography>
          <Typography variant="body1" color="info.main">
            {importProgress.updatedCount} updated
          </Typography>
          {importProgress.failedCount > 0 && (
            <Typography variant="body1" color="error">
              {importProgress.failedCount} failed
            </Typography>
          )}
        </Stack>
      </Stack>
    )
  }

  // Import complete — show results
  const created = importResult?.createdCount ?? 0
  const updated = importResult?.updatedCount ?? 0
  const failed = importResult?.failedCount ?? 0
  const warnings = importResult?.warnings ?? []

  return (
    <Stack spacing={4} alignItems="center">
      <CheckCircle sx={{ fontSize: 80, color: 'success.main' }} />
      <Typography variant="h4">Import Complete!</Typography>
      <Box textAlign="center">
        <Stack direction="row" spacing={3} justifyContent="center">
          <Typography variant="h6">
            {created} {pluralize(entityNounPlural, created)} created
          </Typography>
          {updated > 0 && (
            <Typography variant="h6">
              {updated} {pluralize(entityNounPlural, updated)} updated
            </Typography>
          )}
        </Stack>
        {failed > 0 && (
          <Typography variant="body1" color="error" mt={1}>
            {failed} row{failed !== 1 ? 's' : ''} failed
          </Typography>
        )}
      </Box>
      {warnings.length > 0 && (
        <Stack spacing={1} width="100%" maxWidth={600}>
          {warnings.map((warning, i) => (
            <Alert key={i} severity="warning" icon={<Warning />}>
              {warning}
            </Alert>
          ))}
        </Stack>
      )}
      <Stack direction="row" spacing={2}>
        {showEnrichmentOption && (
          <Button variant="outlined" size="large" onClick={onEnrichMissingProperties}>
            {formatMessage({ id: 'import.enrichment.title' })}
          </Button>
        )}
        <Button variant="contained" onClick={onComplete}>
          {completeButtonLabel}
        </Button>
      </Stack>
    </Stack>
  )
}

export default CompleteStep
